import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Wand2, Copy, Hash } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

const CHANNELS = [
  { key: "instagram", label: "Instagram caption", hint: "punchy, emojis, hashtags" },
  { key: "facebook", label: "Facebook post", hint: "friendly, clear CTA" },
  { key: "email", label: "Email subject + teaser", hint: "high open-rate" },
  { key: "sms", label: "SMS blast", hint: "short, urgent" },
];

export default function AdminMarketing() {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compId, setCompId] = useState("");
  const [channel, setChannel] = useState("instagram");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/competitions").then(({ data }) => { setComps(data || []); if (data?.[0]) setCompId(data[0].id); }).catch(() => {}).finally(() => setLoading(false)); }, []);

  const generate = async () => {
    const comp = comps.find((c) => c.id === compId);
    const ch = CHANNELS.find((c) => c.key === channel);
    const base = comp ? `${comp.title} — prize worth £${comp.prize_value}, tickets from £${comp.ticket_price}` : extra;
    if (!base && !extra) { toast.error("Pick a competition or add a brief."); return; }
    const prompt = `Write a ${ch.label} (${ch.hint}) to promote this prize competition: ${base}. ${extra}`.trim();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai/describe", { prompt });
      setResult({ title: data.title, copy: data.copy, tags: data.tags || [] });
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied to clipboard."); };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-marketing">
      <div className="flex items-center gap-2"><Megaphone className="h-6 w-6 text-[var(--neon-pink)]" /><h2 className="font-display uppercase text-2xl text-white">AI Marketing Studio</h2></div>
      <p className="mt-1 text-sm text-[var(--text-muted)]">Generate ready-to-post promo copy for any competition, powered by your backend AI.</p>

      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Competition</label>
            <select value={compId} onChange={(e) => setCompId(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="marketing-competition">
              {comps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white" data-testid="marketing-channel">
              {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Optional angle, e.g. 'ending tonight, last 100 tickets'" className="mt-3 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="marketing-extra" />
        <NeonButton variant="pink" className="mt-3" onClick={generate} loading={busy} data-testid="marketing-generate"><Wand2 className="h-4 w-4" /> Generate Promo</NeonButton>
      </NeonCard>

      {result && (
        <NeonCard variant="soft" className="mt-4" innerClassName="p-5" >
          <div className="flex items-center justify-between">
            <p className="font-display uppercase text-lg text-white">{result.title}</p>
            <NeonButton variant="outlineCyan" className="px-3 py-2 text-xs" onClick={() => copy(result.copy)}><Copy className="h-4 w-4" /> Copy</NeonButton>
          </div>
          <p className="mt-3 text-[var(--foreground)] whitespace-pre-wrap" data-testid="marketing-result">{result.copy}</p>
          {result.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {result.tags.map((t, i) => <Pill key={i} color="cyan"><Hash className="h-3 w-3" />{t}</Pill>)}
            </div>
          )}
        </NeonCard>
      )}
    </div>
  );
}
